import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import cartStore from '../utils/cartStore';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';
import { toast } from 'sonner';
import { Trash2, ShoppingBag } from 'lucide-react';

const Cart = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const { formatPrice } = useCurrency();
  const [cartItems, setCartItems] = useState([]);

  useEffect(() => {
    setCartItems(cartStore.getCart());
    const handleUpdate = () => setCartItems(cartStore.getCart());
    window.addEventListener('cartUpdated', handleUpdate);
    return () => window.removeEventListener('cartUpdated', handleUpdate);
  }, []);

  const handleUpdateQuantity = (itemId, quantity) => {
    if (quantity < 1) return;
    cartStore.updateQuantity(itemId, quantity);
    setCartItems(cartStore.getCart());
  };

  const handleRemove = (itemId) => {
    cartStore.removeItem(itemId);
    setCartItems(cartStore.getCart());
    toast.success(t('removedFromCart'));
  };

  const calculateTotal = () => {
    return cartItems.reduce((sum, item) => {
      const price = item.product?.discount_price || item.product?.price || 0;
      return sum + price * item.quantity;
    }, 0);
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="cart-page">
      <SEO title="Shopping Cart" noindex />
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-cormorant text-4xl sm:text-5xl mb-8" data-testid="cart-heading">{t('shoppingCart')}</h1>

        {cartItems.length === 0 ? (
          <div className="text-center py-20" data-testid="empty-cart">
            <ShoppingBag className="w-16 h-16 mx-auto text-[#8C8577] mb-4" />
            <p className="font-outfit text-[#8C8577] mb-4">{t('cartEmpty')}</p>
            <button onClick={() => navigate('/shop')} className="bg-[#C29B57] text-white px-6 py-2 rounded-md hover:bg-[#99733D] transition-colors" data-testid="continue-shopping-empty">{t('continueShopping')}</button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Cart Items */}
            <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
              {cartItems.map((item) => {
                const price = item.product?.discount_price || item.product?.price || 0;
                return (
                  <div key={item.id} className="bg-white p-4 rounded-lg flex gap-4" data-testid={`cart-item-${item.id}`}>
                    <div className="w-24 h-24 bg-[#F3EFE6] rounded overflow-hidden flex-shrink-0">
                      {item.product?.images && item.product.images.length > 0 && (
                        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${item.product.images[0]}`} alt={item.product.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-cormorant text-xl mb-1">{item.product?.name}</h3>
                      <p className="font-outfit text-sm text-[#8C8577] mb-2">{item.product?.category}</p>
                      {item.color && <p className="font-outfit text-sm">Color: {item.color}</p>}
                      {item.size && <p className="font-outfit text-sm">Size: {item.size}</p>}
                      <p className="font-outfit text-lg text-[#C29B57] mt-2">{formatPrice(price)}</p>
                    </div>
                    <div className="flex flex-col items-end justify-between">
                      <button onClick={() => handleRemove(item.id)} className="text-[#9E2A2B] hover:text-[#7A1F1F]" data-testid={`remove-${item.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <div className="flex items-center gap-2">
                        <button onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)} className="w-8 h-8 border border-[#EBE4D5] flex items-center justify-center" data-testid={`decrease-${item.id}`}>-</button>
                        <span className="font-outfit text-sm w-8 text-center" data-testid={`quantity-${item.id}`}>{item.quantity}</span>
                        <button onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)} className="w-8 h-8 border border-[#EBE4D5] flex items-center justify-center" data-testid={`increase-${item.id}`}>+</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Summary */}
            <div className="bg-white p-6 rounded-lg h-fit" data-testid="order-summary">
              <h2 className="font-cormorant text-2xl mb-4">{t('orderSummary')}</h2>
              <div className="space-y-3 mb-6">
                <div className="flex justify-between font-outfit text-sm">
                  <span>{t('subtotal')}</span>
                  <span data-testid="subtotal">{formatPrice(calculateTotal())}</span>
                </div>
                <div className="flex justify-between font-outfit text-sm">
                  <span>{t('shipping')}</span>
                  <span className="text-[#3A5A40]">{t('free')}</span>
                </div>
                <div className="border-t border-[#EBE4D5] pt-3 flex justify-between font-outfit text-lg">
                  <span>{t('total')}</span>
                  <span className="text-[#C29B57]" data-testid="total">{formatPrice(calculateTotal())}</span>
                </div>
              </div>
              <button onClick={() => navigate('/checkout')} className="w-full bg-[#1A1A18] text-white hover:bg-[#C29B57] transition-colors duration-300 rounded-none px-8 py-4 font-outfit uppercase tracking-wider text-sm" data-testid="checkout-button">{t('proceedToCheckout')}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Cart;
